<template>
  <div class="about-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <h1 data-aos="fade-down">Qui sommes-nous ?</h1>
        <p data-aos="fade-up" data-aos-delay="200">Cabinet conseil en Management des Ressources Humaines depuis 2012</p>
      </div>
    </section>

    <div class="container">

      <!-- Presentation Section -->
      <section class="section-block" data-aos="fade-up">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <div class="section-label">Notre histoire</div>
            <h2>PREFERE PLUS, votre partenaire RH de confiance</h2>
            <p class="lead-text">
              Cree en <strong>2012</strong>, le cabinet PREFERE PLUS developpe et distribue des solutions adaptees
              et sur mesure en <strong>Management des Ressources Humaines</strong>.
            </p>
            <p>
              Notre large gamme de solutions en GRH et de services associes nous permet de vous accompagner
              sur l'ensemble de vos problematiques en ressources humaines, formation, recrutement,
              conseil et bien plus encore.
            </p>
            <div class="highlight-box">
              <div class="highlight-item">
                <i class="fas fa-calendar-check"></i>
                <div>
                  <strong>Depuis 2012</strong>
                  <span>au service des entreprises</span>
                </div>
              </div>
              <div class="highlight-item">
                <i class="fas fa-clock"></i>
                <div>
                  <strong>+2 000 heures</strong>
                  <span>de formation dispensees</span>
                </div>
              </div>
              <div class="highlight-item">
                <i class="fas fa-handshake"></i>
                <div>
                  <strong>+50 partenaires</strong>
                  <span>multinationales et institutions</span>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-6" data-aos="fade-left" data-aos-delay="200">
            <div class="about-image-wrapper">
              <img src="../assets/img/prefereplus.png" alt="Equipe PREFERE PLUS" class="about-image" />
              <div class="image-decoration"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- Domaines d'expertise -->
      <section class="section-block expertise-section" data-aos="fade-up">
        <div class="text-center mb-5">
          <div class="section-label center">Nos competences</div>
          <h2>Domaines d'expertise</h2>
          <p class="section-subtitle">Une expertise complete pour repondre a tous vos besoins en ressources humaines</p>
        </div>
        <div class="expertise-grid">
          <div
            v-for="(item, index) in expertiseDomains"
            :key="index"
            class="expertise-card"
            :data-aos="index % 2 === 0 ? 'fade-right' : 'fade-left'"
            :data-aos-delay="index * 50"
          >
            <div class="expertise-icon">
              <i :class="item.icon"></i>
            </div>
            <div class="expertise-content">
              <h4>{{ item.title }}</h4>
              <p>{{ item.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Valeurs DEFI -->
      <section class="section-block values-section" data-aos="fade-up">
        <div class="text-center mb-5">
          <div class="section-label center">Ce qui nous anime</div>
          <h2>Nos valeurs : <span class="text-accent">D.E.F.I</span></h2>
        </div>
        <div class="values-grid">
          <div v-for="(value, index) in values" :key="index" class="value-card" :data-aos="'zoom-in'" :data-aos-delay="index * 150">
            <div class="value-letter" :style="{ background: value.color }">{{ value.letter }}</div>
            <h4>{{ value.title }}</h4>
            <p>{{ value.description }}</p>
          </div>
        </div>
      </section>

      <!-- Equipe / Formateurs -->
      <section class="section-block team-section" data-aos="fade-up">
        <div class="text-center mb-5">
          <div class="section-label center">Notre equipe</div>
          <h2>Nos formateurs seniors</h2>
          <p class="section-subtitle">Des experts reconnus avec plus de 20 ans d'experience</p>
        </div>
        <div class="row">
          <div class="col-lg-6 mb-4" v-for="(trainer, index) in trainers" :key="index" :data-aos="index === 0 ? 'fade-right' : 'fade-left'">
            <div class="trainer-card">
              <div class="trainer-icon">
                <i class="fas fa-user-tie"></i>
              </div>
              <div class="trainer-info">
                <h4>{{ trainer.name }}</h4>
                <p class="trainer-title">{{ trainer.title }}</p>
                <p class="trainer-bio">{{ trainer.bio }}</p>
                <div class="trainer-tags">
                  <span v-for="(tag, i) in trainer.tags" :key="i" class="tag">{{ tag }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Partenaires -->
      <section class="section-block partners-section" data-aos="fade-up">
        <div class="text-center mb-5">
          <div class="section-label center">Ils nous font confiance</div>
          <h2>Nos partenaires et references</h2>
          <p class="section-subtitle">Plus de 50 entreprises, multinationales et institutions nous font confiance</p>
        </div>

        <!-- Logos -->
        <div class="partners-logos-grid">
          <div
            v-for="(partner, index) in partners"
            :key="'logo-' + index"
            class="partner-logo"
            :title="partner.name"
          >
            <img :src="partner.logo" :alt="partner.name" loading="lazy" @error="onLogoError" />
          </div>
        </div>

        <!-- Autres partenaires (texte) -->
        <div class="partners-extra">
          <p class="partners-extra-label">Et bien d'autres :</p>
          <div class="partners-track">
            <span v-for="(partner, index) in otherPartners" :key="'txt-' + index" class="partner-badge">
              {{ partner }}
            </span>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="section-block cta-section" data-aos="fade-up">
        <div class="cta-box">
          <h2>Avec nous, boostez votre performance</h2>
          <p>Contactez-nous pour discuter de vos besoins en recrutement, formation ou conseil en GRH</p>
          <div class="cta-buttons">
            <router-link to="/contact" class="btn btn-cta-primary">
              <i class="fas fa-envelope me-2"></i>Nous contacter
            </router-link>
            <router-link to="/job-offers" class="btn btn-cta-secondary">
              <i class="fas fa-briefcase me-2"></i>Voir nos offres
            </router-link>
          </div>
          <div class="cta-contacts">
            <a href="tel:+22901401188884" class="cta-contact-item">
              <i class="fas fa-phone-alt"></i> 01 40 11 88 84
            </a>
            <a href="mailto:prefereplus@gmail.com" class="cta-contact-item">
              <i class="fas fa-envelope"></i> prefereplus@gmail.com
            </a>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";

export default {
  name: "AboutPage",
  data() {
    return {
      expertiseDomains: [
        { icon: "fas fa-user-tie", title: "Recrutement & Chasse de tete", description: "Mise a disposition de CV, preselection sur dossier, appui aux entretiens, tests psychotechniques." },
        { icon: "fas fa-graduation-cap", title: "Formation & Coaching", description: "Formations inter et intra entreprises, coaching et accompagnement individuel personnalise." },
        { icon: "fas fa-tools", title: "Etudes & Developpement d'outils", description: "Plan de formation, convention collective, plan strategique, GPEC, audit social et organisationnel." },
        { icon: "fas fa-users-cog", title: "Conseils en Gestion & Assistance GRH", description: "Gestion du personnel, licenciements, formalites sociales et fiscales, coaching professionnel." },
        { icon: "fas fa-chart-line", title: "Evaluation de projets & programmes", description: "Etudes prospectives, evaluation de projets et programmes de developpement." },
        { icon: "fas fa-lightbulb", title: "Recherche & Developpement", description: "Innovation en management RH, developpement de nouvelles methodologies et outils." },
        { icon: "fas fa-people-arrows", title: "Interim & Mise a disposition", description: "Mise a disposition de personnel qualifie pour vos besoins temporaires ou permanents." },
        { icon: "fas fa-file-contract", title: "Documents administratifs & RH", description: "Elaboration de documents, manuels d'employe, reglement interieur, politiques RH." },
        { icon: "fas fa-cogs", title: "Consulting & Ingenierie RH", description: "Ingenierie de formation, relooking CV, conception de plans de formation annuels." },
        { icon: "fas fa-calculator", title: "Gestion financiere & comptable", description: "Accompagnement en gestion financiere et comptabilite d'entreprise." },
        { icon: "fas fa-boxes", title: "Inventaire & Gestion de stock", description: "Solutions d'inventaire et optimisation de la gestion des stocks." },
      ],
      values: [
        { letter: "D", title: "Durabilite", description: "Construire des partenariats solides et des solutions perennes pour nos clients.", color: "linear-gradient(135deg, #29b3cd, #1d9ed9)" },
        { letter: "E", title: "Expertise", description: "S'appuyer sur +20 ans d'experience et des formateurs certifies internationalement.", color: "linear-gradient(135deg, #e95d16, #bc6a3c)" },
        { letter: "F", title: "Flexibilite", description: "Adapter nos solutions a chaque contexte et chaque besoin specifique.", color: "linear-gradient(135deg, #29b3cd, #1d9ed9)" },
        { letter: "I", title: "Innovation", description: "Integrer les dernieres avancees en management RH et outils digitaux.", color: "linear-gradient(135deg, #e95d16, #bc6a3c)" },
      ],
      trainers: [
        {
          name: "Edgard BADA",
          title: "Formateur Senior - +20 ans d'experience",
          bio: "Juriste, titulaire d'un Master en GRH, Master en Gestion des Entreprises et Master en etudes prospectives. Certifie Manager RH par HEC Montreal et Save the Children. Plus de 2 000 heures de formation au profit de multinationales et institutions.",
          tags: ["Management", "GRH", "Droit du travail", "Formation", "Coaching"]
        },
        {
          name: "GAHOU Leonie",
          title: "Formatrice Senior - Docteure en Sante Publique",
          bio: "Chef de Division des Etablissements Prives de Sante du Benin. Doctorat en Sante Publique, Licence en Sante et Securite au Travail. Plus de 20 ans d'experience en hygiene hospitaliere, prevention et controle des infections.",
          tags: ["HSE", "Sante au travail", "Securite", "Qualite", "Prevention"]
        }
      ],
      partnerFiles: [
        { name: "MTN Benin", file: "MTNBENIN.png" },
        { name: "GIZ", file: "giz.png" },
        { name: "Sodeco", file: "sodeco.png" },
        { name: "Orabank", file: "orabank.jpg" },
        { name: "SBIN", file: "sbin.png" },
        { name: "Bank of Africa", file: "BOA.png" },
        { name: "Benin Control", file: "BC.png" },
        { name: "SIRAT", file: "SIRAT.png" },
        { name: "CRS", file: "CSR.png" },
        { name: "ATT", file: "ATT.png" },
        { name: "CNLS-TP", file: "CNLS.png" },
        { name: "CMA-Benin", file: "CMA.png" },
        { name: "ANSSI", file: "anssi.png" },
        { name: "ENGIE", file: "engie.png" },
        { name: "SIMAU", file: "simau.png" }
      ],
      otherPartners: [
        "Amnesty International", "Swisscontact", "BRMN", "HeidelbergCement",
        "CMMB", "Coca-Cola", "Dataneo", "ASSI", "MSH", "ARESS", "FAO",
        "WAPCO Benin", "SHB", "BRS", "MTN Congo", "Diamond Bank",
        "CCIB", "MEEM", "FODEFCA", "Fondation Espace Afrique",
        "SFP", "ADELAC", "ABMeD", "LNB", "Medecins du Monde",
        "BEST Experts", "ABMS", "RACINES", "SONIMEX"
      ]
    };
  },
  computed: {
    partners() {
      const base = process.env.VUE_APP_API_ENDPOINT || '';
      return this.partnerFiles.map(p => ({
        name: p.name,
        logo: `${base}/images/partners/${p.file}`
      }));
    }
  },
  mounted() {
    document.querySelector("body").style.backgroundColor = "#ffffff";
    // AOS initialise globalement dans main.js
    this.$nextTick(() => AOS.refresh());
  },
  methods: {
    onLogoError(event) {
      // Masquer l'image si elle ne charge pas
      if (event && event.target) {
        event.target.closest('.partner-logo').style.display = 'none';
      }
    }
  }
};
</script>

<style scoped>
.about-page {
  font-family: 'Poppins', sans-serif;
  color: #2c3e50;
}

/* Hero */
.hero {
  background: url('../assets/img/bg-navbar.jpg') center/cover no-repeat;
  height: 45vh;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  position: relative;
}

.hero-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(135deg, rgba(41,179,205,0.85), rgba(233,93,22,0.7));
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  padding: 0 1rem;
}

.hero-content h1 {
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.hero-content p {
  font-size: 1.2rem;
  opacity: 0.95;
  font-weight: 300;
}

/* Sections */
.section-block {
  padding: 4rem 0;
}

.section-block + .section-block {
  border-top: 1px solid #eef2f6;
}

.section-label {
  display: inline-block;
  background: linear-gradient(135deg, #29b3cd, #1d9ed9);
  color: white;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1rem;
}

.section-label.center {
  margin-left: auto;
  margin-right: auto;
}

h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.text-accent {
  color: #e95d16;
}

.section-subtitle {
  color: #6c757d;
  font-size: 1.05rem;
  max-width: 600px;
  margin: 0 auto;
}

.lead-text {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4a5568;
}

/* Highlight box */
.highlight-box {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.highlight-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.2rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #29b3cd;
  transition: transform 0.3s;
}

.highlight-item:hover {
  transform: translateX(5px);
}

.highlight-item i {
  font-size: 1.5rem;
  color: #29b3cd;
  width: 30px;
  text-align: center;
}

.highlight-item strong {
  display: block;
  color: #2c3e50;
  font-size: 1rem;
}

.highlight-item span {
  color: #6c757d;
  font-size: 0.85rem;
}

/* About image */
.about-image-wrapper {
  position: relative;
  padding: 2rem;
}

.about-image {
  width: 100%;
  max-width: 450px;
  border-radius: 16px;
  position: relative;
  z-index: 2;
}

.image-decoration {
  position: absolute;
  top: 0; right: 0;
  width: 70%;
  height: 70%;
  background: linear-gradient(135deg, #29b3cd20, #e95d1620);
  border-radius: 16px;
  z-index: 1;
}

/* Expertise */
.expertise-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.2rem;
}

.expertise-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.2rem;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #eef2f6;
  transition: all 0.3s ease;
}

.expertise-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  border-color: #29b3cd;
}

.expertise-icon {
  width: 48px;
  height: 48px;
  min-width: 48px;
  background: linear-gradient(135deg, #29b3cd15, #1d9ed915);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.expertise-icon i {
  font-size: 1.2rem;
  color: #29b3cd;
}

.expertise-content h4 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
  color: #2c3e50;
}

.expertise-content p {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
}

/* Values */
.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
}

.value-card {
  text-align: center;
  padding: 2rem 1.5rem;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #eef2f6;
  transition: all 0.3s ease;
}

.value-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.1);
}

.value-letter {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 auto 1rem;
}

.value-card h4 {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.value-card p {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
}

/* Team */
.trainer-card {
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #eef2f6;
  transition: all 0.3s ease;
  height: 100%;
}

.trainer-card:hover {
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
}

.trainer-icon {
  width: 64px;
  height: 64px;
  min-width: 64px;
  background: linear-gradient(135deg, #29b3cd, #1d9ed9);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.trainer-info h4 {
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.2rem;
}

.trainer-title {
  color: #29b3cd;
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.trainer-bio {
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.6;
}

.trainer-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.8rem;
}

.tag {
  display: inline-block;
  padding: 3px 10px;
  background: #29b3cd12;
  color: #29b3cd;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Partners */
.partners-logos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1.5rem;
  align-items: center;
  justify-items: center;
  padding: 1rem 0 2.5rem;
}

.partner-logo {
  width: 100%;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #eef2f6;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.partner-logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: grayscale(100%);
  opacity: 0.75;
  transition: filter 0.3s ease, opacity 0.3s ease, transform 0.3s ease;
}

.partner-logo:hover {
  border-color: #29b3cd;
  box-shadow: 0 6px 16px rgba(41, 179, 205, 0.15);
  transform: translateY(-3px);
}

.partner-logo:hover img {
  filter: grayscale(0);
  opacity: 1;
  transform: scale(1.05);
}

.partners-extra {
  text-align: center;
  padding-top: 1rem;
  border-top: 1px dashed #e2e8f0;
}

.partners-extra-label {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 1rem;
  font-weight: 500;
}

.partners-track {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.6rem;
}

.partner-badge {
  display: inline-block;
  padding: 6px 14px;
  background: #f8f9fa;
  border: 1px solid #eef2f6;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #4a5568;
  transition: all 0.3s ease;
}

.partner-badge:hover {
  background: #29b3cd;
  color: white;
  border-color: #29b3cd;
  transform: translateY(-2px);
}

@media (max-width: 576px) {
  .partners-logos-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  .partner-logo {
    height: 75px;
  }
}

/* CTA */
.cta-section {
  padding-bottom: 5rem;
}

.cta-box {
  text-align: center;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, #29b3cd, #1d9ed9);
  border-radius: 20px;
  color: white;
}

.cta-box h2 {
  color: white;
  font-size: 1.8rem;
}

.cta-box p {
  color: rgba(255,255,255,0.9);
  font-size: 1rem;
  margin-bottom: 1.5rem;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.btn-cta-primary {
  background: #e95d16;
  color: white;
  padding: 12px 28px;
  border-radius: 50px;
  font-weight: 600;
  border: 2px solid #e95d16;
  transition: all 0.3s;
}

.btn-cta-primary:hover {
  background: white;
  color: #e95d16;
}

.btn-cta-secondary {
  background: transparent;
  color: white;
  padding: 12px 28px;
  border-radius: 50px;
  font-weight: 600;
  border: 2px solid white;
  transition: all 0.3s;
}

.btn-cta-secondary:hover {
  background: white;
  color: #29b3cd;
}

.cta-contacts {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.cta-contact-item {
  color: rgba(255,255,255,0.9);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s;
}

.cta-contact-item:hover {
  color: white;
}

.cta-contact-item i {
  margin-right: 0.3rem;
}

/* Responsive */
@media (max-width: 991px) {
  .hero-content h1 { font-size: 2.2rem; }
  .expertise-grid { grid-template-columns: 1fr; }
  .about-image-wrapper { text-align: center; padding: 1rem; }
}

@media (max-width: 768px) {
  .hero { height: 35vh; min-height: 250px; }
  .hero-content h1 { font-size: 1.8rem; }
  .hero-content p { font-size: 1rem; }
  .section-block { padding: 2.5rem 0; }
  h2 { font-size: 1.6rem; }
  .values-grid { grid-template-columns: repeat(2, 1fr); }
  .trainer-card { flex-direction: column; align-items: center; text-align: center; }
  .cta-contacts { flex-direction: column; gap: 0.8rem; }
  .expertise-grid { grid-template-columns: 1fr; }
}

@media (max-width: 575px) {
  .hero-content h1 { font-size: 1.4rem; }
  h2 { font-size: 1.3rem; }
  .section-block { padding: 2rem 0; }
  .partner-logo { height: 70px; }
}

@media (max-width: 480px) {
  .values-grid { grid-template-columns: 1fr; }
}
</style>
